/**
 * Phase 20.5: Supabase Realtime subscription manager.
 *
 * Connects to Postgres replication events for the active workspace and
 * delivers them to the project store as remote mutations. RLS enforces
 * which rows the user can see; we subscribe to all events on the relevant
 * tables and let the database filter.
 *
 * Lifecycle:
 *   - bootstrap(orgId) → call subscribeToWorkspace(orgId)
 *   - switchOrg(newOrgId) → call unsubscribeAll() then subscribeToWorkspace(newOrgId)
 *   - signOut → unsubscribeAll()
 *
 * Echo handling: when the local user makes a change, the change flows back
 * via Realtime. We use last-write-wins at the row level — the echo merges
 * the same data back in, which is a no-op visually. No client-id tagging
 * for v1; if conflict resolution becomes an issue, that's the future fix.
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  fromProjectRow,
  fromTaskRow,
  fromRiskRow,
  fromIssueRow,
  fromChangeRequestRow,
  fromBaselineRow,
  type ProjectRow,
  type TaskRow,
  type RiskRow,
  type IssueRow,
  type ChangeRequestRow,
  type BaselineRow,
} from '@/lib/api/mappers';
import { fromPortfolioRow, type PortfolioRow, fromProgramRow, type ProgramRow } from '@/lib/api/index';

/**
 * Handlers for remote events. The store implements these to merge events
 * into local state WITHOUT triggering an outbound API write (the change
 * came FROM the API).
 */
export interface RealtimeHandlers {
  // Projects
  onProjectInsert: (projectId: string, project: ReturnType<typeof fromProjectRow>) => void;
  onProjectUpdate: (projectId: string, project: ReturnType<typeof fromProjectRow>) => void;
  onProjectDelete: (projectId: string) => void;

  // Nested entities — caller must locate the project by projectId
  onTaskInsert: (projectId: string, task: ReturnType<typeof fromTaskRow>) => void;
  onTaskUpdate: (projectId: string, task: ReturnType<typeof fromTaskRow>) => void;
  onTaskDelete: (projectId: string, taskId: number) => void;

  onRiskInsert: (projectId: string, risk: ReturnType<typeof fromRiskRow>) => void;
  onRiskUpdate: (projectId: string, risk: ReturnType<typeof fromRiskRow>) => void;
  onRiskDelete: (projectId: string, riskId: string) => void;

  onIssueInsert: (projectId: string, issue: ReturnType<typeof fromIssueRow>) => void;
  onIssueUpdate: (projectId: string, issue: ReturnType<typeof fromIssueRow>) => void;
  onIssueDelete: (projectId: string, issueId: string) => void;

  onChangeInsert: (projectId: string, change: ReturnType<typeof fromChangeRequestRow>) => void;
  onChangeUpdate: (projectId: string, change: ReturnType<typeof fromChangeRequestRow>) => void;
  onChangeDelete: (projectId: string, changeId: string) => void;

  onBaselineInsert: (projectId: string, baseline: ReturnType<typeof fromBaselineRow>) => void;
  onBaselineUpdate: (projectId: string, baseline: ReturnType<typeof fromBaselineRow>) => void;
  onBaselineDelete: (projectId: string, baselineId: string) => void;

  // Portfolios (workspace-level, not nested)
  onPortfolioInsert: (portfolio: ReturnType<typeof fromPortfolioRow>) => void;
  onPortfolioUpdate: (portfolio: ReturnType<typeof fromPortfolioRow>) => void;
  onPortfolioDelete: (portfolioId: string) => void;

  // Programs (workspace-level, not nested)
  onProgramInsert: (program: ReturnType<typeof fromProgramRow>) => void;
  onProgramUpdate: (program: ReturnType<typeof fromProgramRow>) => void;
  onProgramDelete: (programId: string) => void;
}

// Active channels — module-level state, not exposed via store
let channels: RealtimeChannel[] = [];

/**
 * Subscribe to all Realtime channels for the given workspace.
 * Idempotent: if there are existing channels, they are cleaned up first.
 */
export function subscribeToWorkspace(orgId: string, handlers: RealtimeHandlers): void {
  if (!supabase) {
    // No Supabase configured (localStorage mode) — no-op
    return;
  }

  // Clean up any existing subscriptions first
  unsubscribeAll();

  // We don't bother filtering server-side. RLS handles row visibility based
  // on the user's org membership. Realtime will only deliver rows the user
  // can see anyway, so server-side filter is redundant for v1.

  // ---- Projects ----
  const projectsChannel = supabase
    .channel(`org:${orgId}:projects`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'projects' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as ProjectRow;
            // Only handle if it belongs to this org (RLS should already filter,
            // but double-check for cross-org leakage safety)
            // The projects table has organization_id; check it.
            if ((row as ProjectRow & { organization_id?: string }).organization_id !== orgId) return;
            handlers.onProjectInsert(row.id, fromProjectRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as ProjectRow;
            handlers.onProjectUpdate(row.id, fromProjectRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<ProjectRow>;
            if (old.id) handlers.onProjectDelete(old.id);
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(projectsChannel);

  // ---- Tasks ----
  const tasksChannel = supabase
    .channel(`org:${orgId}:tasks`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'tasks' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as TaskRow;
            handlers.onTaskInsert(row.project_id, fromTaskRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as TaskRow;
            handlers.onTaskUpdate(row.project_id, fromTaskRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<TaskRow>;
            if (old.project_id && typeof old.task_id === 'number') {
              handlers.onTaskDelete(old.project_id, old.task_id);
            }
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(tasksChannel);

  // ---- Risks ----
  const risksChannel = supabase
    .channel(`org:${orgId}:risks`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'risks' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as RiskRow;
            handlers.onRiskInsert(row.project_id, fromRiskRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as RiskRow;
            handlers.onRiskUpdate(row.project_id, fromRiskRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<RiskRow>;
            if (old.project_id && old.id) {
              handlers.onRiskDelete(old.project_id, old.id);
            }
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(risksChannel);

  // ---- Issues ----
  const issuesChannel = supabase
    .channel(`org:${orgId}:issues`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'issues' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as IssueRow;
            handlers.onIssueInsert(row.project_id, fromIssueRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as IssueRow;
            handlers.onIssueUpdate(row.project_id, fromIssueRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<IssueRow>;
            if (old.project_id && old.id) {
              handlers.onIssueDelete(old.project_id, old.id);
            }
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(issuesChannel);

  // ---- Change Requests ----
  const changesChannel = supabase
    .channel(`org:${orgId}:change_requests`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'change_requests' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as ChangeRequestRow;
            handlers.onChangeInsert(row.project_id, fromChangeRequestRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as ChangeRequestRow;
            handlers.onChangeUpdate(row.project_id, fromChangeRequestRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<ChangeRequestRow>;
            if (old.project_id && old.id) {
              handlers.onChangeDelete(old.project_id, old.id);
            }
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(changesChannel);

  // ---- Baselines ----
  const baselinesChannel = supabase
    .channel(`org:${orgId}:baselines`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'baselines' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as BaselineRow;
            handlers.onBaselineInsert(row.project_id, fromBaselineRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as BaselineRow;
            handlers.onBaselineUpdate(row.project_id, fromBaselineRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<BaselineRow>;
            if (old.project_id && old.id) {
              handlers.onBaselineDelete(old.project_id, old.id);
            }
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(baselinesChannel);

  // ---- Portfolios ----
  const portfoliosChannel = supabase
    .channel(`org:${orgId}:portfolios`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'portfolios' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as PortfolioRow;
            if (row.organization_id !== orgId) return;
            handlers.onPortfolioInsert(fromPortfolioRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as PortfolioRow;
            handlers.onPortfolioUpdate(fromPortfolioRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<PortfolioRow>;
            if (old.id) handlers.onPortfolioDelete(old.id);
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(portfoliosChannel);

  // ---- Programs ----
  const programsChannel = supabase
    .channel(`org:${orgId}:programs`)
    .on(
      'postgres_changes' as const,
      { event: '*', schema: 'public', table: 'programs' },
      (payload) => {
        switch (payload.eventType) {
          case 'INSERT': {
            const row = payload.new as ProgramRow;
            if (row.organization_id !== orgId) return;
            handlers.onProgramInsert(fromProgramRow(row));
            break;
          }
          case 'UPDATE': {
            const row = payload.new as ProgramRow;
            handlers.onProgramUpdate(fromProgramRow(row));
            break;
          }
          case 'DELETE': {
            const old = payload.old as Partial<ProgramRow>;
            if (old.id) handlers.onProgramDelete(old.id);
            break;
          }
        }
      },
    )
    .subscribe();
  channels.push(programsChannel);
}

/**
 * Tear down all active Realtime subscriptions. Safe to call repeatedly.
 */
export function unsubscribeAll(): void {
  if (!supabase) return;
  for (const ch of channels) {
    supabase.removeChannel(ch);
  }
  channels = [];
}

/** For diagnostics: how many channels are currently active. */
export function activeChannelCount(): number {
  return channels.length;
}
